import React, { useState } from 'react';
import { Loader2, PlusCircle, Info } from 'lucide-react';
import { MARKET_COUNTRIES, MARKET_VERTICALS } from '../../services/marketCountries';

/**
 * Shown when SEMrush has no data for a domain, so nothing can be derived and the
 * operator has to say which market and vertical it belongs to.
 *
 * Exactly one country: with no SEMrush geo breakdown there is nothing to split
 * the traffic across, so the single chosen market carries all of it.
 */
const ManualAffiliateDetails = ({ domain, busy, onConfirm, onCancel, compact = false }) => {
  const [country, setCountry] = useState('');
  const [vertical, setVertical] = useState('');

  const ready = country && vertical;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-800/50 dark:bg-amber-900/10">
      <div className="mb-3 flex items-start gap-2">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
            SEMrush has no data for {domain}
          </p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
            Pick the market and vertical and it will be added anyway. Its traffic is assigned to the one country you
            choose.
          </p>
        </div>
      </div>

      <div className={`grid gap-3 ${compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'}`}>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
            Country <span className="text-red-500">*</span>
          </label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            disabled={busy}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          >
            <option value="">Select a country…</option>
            {MARKET_COUNTRIES.map((c) => (
              <option key={c.code} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
            Vertical <span className="text-red-500">*</span>
          </label>
          <select
            value={vertical}
            onChange={(e) => setVertical(e.target.value)}
            disabled={busy}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          >
            <option value="">Select a vertical…</option>
            {MARKET_VERTICALS.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onConfirm({ country, vertical })}
          disabled={!ready || busy}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
          {busy ? 'Adding…' : 'Add affiliate'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-white/60 disabled:opacity-60 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default ManualAffiliateDetails;
